<script setup>
import { computed, nextTick, onMounted } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import {
  barColors,
  categories,
  chartColors,
  expandedCategories,
} from './yongsinFiveChart'

const props = defineProps({
  hanDefaultData: Object,
  hanjijangData: Object,
  chartColor: Object,
})

const fiveElementColors = {
  목: 'rgba(var(--v-theme-fiveColor03), 1)',
  화: 'rgba(var(--v-theme-fiveColor01), 1)',
  토: 'rgba(var(--v-theme-fiveColor05), 1)',
  금: 'rgba(var(--v-theme-fiveColor04), 1)',
  수: 'rgba(var(--v-theme-fiveColor02), 1)',
}

const baseBarData = computed(() => {
  const data = props.hanDefaultData
  const colorMap = props.chartColor || {}

  return categories.map((key) => {
    const element = colorMap[key]
    const fillColor = fiveElementColors[element] || '#ccc' 
    return {
      x: key,
      y: data?.[key] ?? 0,
      fillColor,
    }
  })
})


const jijangLine = categories.map((key) => ({
  x: key,
  y: props.hanjijangData[key] ?? 0,
}))

const series = computed(() => [
  { name: '기본오행분포', type: 'column', data: baseBarData.value },
  { name: '지장오행분포', type: 'line', data: jijangLine },
])

const labelColors = computed(() =>
  categories.map((key) => {
    const element = props.chartColor?.[key] // 예: "금"
    return fiveElementColors[element] || '#000'
  })
)

const chartOptions = computed(() => {
  const activeColors = chartColors.slice(0, series.value.length)

  return {
    chart: {
      type: 'line',
      stacked: false,
      toolbar: { show: false },
      zoom: { enabled: false },
      parentHeightOffset: 0,
      width: '100%',
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusWhenStacked: 'last',
        columnWidth: '40px',
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: false,
      offsetY: -12,
      style: {
        colors: labelColors.value,
      },
      background: {
        enabled: false,
        foreColor: '#000',
        fill: '#fff',
        padding: 4,
        opacity: 1,
        borderColor: 'transparent',
        dropShadow: {
          enabled: false,
        },
      },
      formatter: (val, { seriesIndex }) => {
        if (seriesIndex === 1) return `${val}%`
        return ''
      },
    },
    colors: activeColors,
    xaxis: {
      categories,
      axisTicks: { show: false },
      axisBorder: { show: false },
      tickPlacement: 'on',
      labels: {
        style: {
          colors: labelColors.value,
          fontFamily: 'Pretendard',
          fontSize: '14px',
          fontWeight: '500',
        },
      },
    },
    yaxis: {
      tickAmount: 4,
      labels: {
        padding: 8,
        formatter: (val) => (val === 0 ? '0' : Math.round(val)),
      },
    },
    legend: {
      position: 'bottom',
      onItemClick: {
        toggleDataSeries: false,
      },
    },
    grid: {
      padding: {
        top: 4,
        bottom: 16,
        left: -4,
      },
    },
    tooltip: { 
      enabled: true,
      y: {
        formatter: (val) => val.toString(),
      },
    },
    stroke: {
      width: [0, 2],
      curve: ['smooth', 'smooth'],
      colors: activeColors,
    },
    markers: {
      size: 4,
      strokeWidth: 2,
      fillOpacity: 1,
      colors: ['#fff'],
      strokeColors: activeColors,
    },
  }
})

const updateChartStyles = () => {
  nextTick(() => {
    const labels = document.querySelectorAll('.apexcharts-data-labels text')
    const tooltipMarkers = document.querySelectorAll('.apexcharts-tooltip-marker')
    const legendMarkers = document.querySelectorAll('.apexcharts-legend-marker')
    const legendText = document.querySelectorAll('.apexcharts-legend-text')

    const urls = [
      '/images/chart/bar-chart__legend.svg',
      '/images/chart/line-chart__legend-orange.svg',
    ]

    // labels.forEach((label, index) => {
    //   const key = categories[index]
    //   label.setAttribute('fill', barColors[key] ?? '#000') 
    //   label.style.fontWeight = '700'
    // })
    labels.forEach((label, index) => {
      const key = categories[index]
      const element = props.chartColor?.[key]
      const color = fiveElementColors[element] || '#000'
      label.setAttribute('fill', color)
      label.style.fontWeight = '700'
    })

    legendMarkers.forEach((marker, index) => {
      marker.style.background = `url(${urls[index]}) no-repeat center/contain`
      marker.style.borderRadius = '0'
      marker.style.width = '22px'
      marker.style.height = '12px'
    })
    tooltipMarkers.forEach((marker, index) => {
      marker.style.background = `url(${urls[index]}) no-repeat center/contain`
      marker.style.borderRadius = '0'
      marker.style.width = '22px'
      marker.style.height = '12px'
    })
    legendText.forEach((el) => {
      el.style.color = '#333'
      el.style.fontSize = '14px'
      el.style.fontFamily = 'Pretendard'
      el.style.fontWeight = '400'
      el.style.lineHeight = '18px'
    })
  })
}

onMounted(updateChartStyles)
</script>

<template>
  <div class="chart-container">
    <VueApexCharts type="line" height="300" :options="chartOptions" :series="series" />
  </div>
</template>

<style scoped lang="scss">
.chart-container {
  width: 100%;
  max-width: 500px;
  overflow: hidden;
}

.chart-container .apexcharts-canvas {
  max-width: 100% !important;
  width: 100% !important;
}

.apexcharts-tooltip {
  border-radius: 5px;
}

.apexcharts-tooltip-marker {
  background-color: transparent !important;
}
</style>
